import { useCallback, useEffect, useRef, useState } from "react";
import type { PlanEvent } from "../../../../shared/types";
import type { FormAnchor } from "../../hooks/use-week-calendar-state";

const DRAG_THRESHOLD_PX = 8;

interface DragDropApi {
  startDrag: (event: PlanEvent, mouseYInColumn: number) => unknown;
  updateDrag: (clientX: number, clientY: number, freeSnap?: boolean) => void;
  commitDrag: () => void;
  startResize: (event: PlanEvent, edge: "top" | "bottom") => void;
  updateResize: (deltaY: number, freeSnap?: boolean) => void;
  commitResize: () => void;
}

interface SlotCreationApi {
  startSlotCreation: (date: string, mouseY: number, freeSnap?: boolean) => void;
  updateSlotCreation: (mouseY: number, freeSnap?: boolean) => void;
  commitSlotCreation: () => {
    date: string;
    startTime: string;
    endTime: string;
  } | null;
  cancelSlotCreation: () => void;
}

interface WeekGridDragHandlersState {
  isCursorGrabbing: boolean;
  justDraggedRef: React.MutableRefObject<boolean>;
  handleDragStart: (
    event: PlanEvent,
    mouseYInColumn: number,
    clientX: number,
    clientY: number,
  ) => void;
  handleResizeStart: (
    event: PlanEvent,
    clientY: number,
    edge: "top" | "bottom",
  ) => void;
  handleSlotCreationMouseDown: (
    date: string,
    mouseY: number,
    columnEl: HTMLDivElement,
  ) => void;
}

export function useWeekGridDragHandlers(
  dd: DragDropApi,
  sc: SlotCreationApi,
  onSlotCreated: (
    date: string,
    startTime: string,
    endTime: string,
    anchor: FormAnchor,
  ) => void,
): WeekGridDragHandlersState {
  const ddRef = useRef<DragDropApi>(dd);
  const scRef = useRef<SlotCreationApi>(sc);
  const onSlotCreatedRef = useRef(onSlotCreated);
  useEffect(() => {
    ddRef.current = dd;
    scRef.current = sc;
    onSlotCreatedRef.current = onSlotCreated;
  }, [dd, sc, onSlotCreated]);

  const resizeStartYRef = useRef<number>(0);
  const [isCursorGrabbing, setIsCursorGrabbing] = useState(false);

  const pendingDragRef = useRef<{
    event: PlanEvent;
    mouseYInColumn: number;
    startX: number;
    startY: number;
    started: boolean;
  } | null>(null);

  const justDraggedRef = useRef(false);

  const moveListenerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const upListenerRef = useRef<(() => void) | null>(null);
  const removePointerListeners = useCallback(() => {
    if (moveListenerRef.current) {
      window.removeEventListener("mousemove", moveListenerRef.current);
      moveListenerRef.current = null;
    }
    if (upListenerRef.current) {
      window.removeEventListener("mouseup", upListenerRef.current);
      upListenerRef.current = null;
    }
  }, []);

  const handleDragStart = useCallback(
    (
      event: PlanEvent,
      mouseYInColumn: number,
      clientX: number,
      clientY: number,
    ) => {
      // Nettoyer un éventuel drag précédent non terminé
      removePointerListeners();

      pendingDragRef.current = {
        event,
        mouseYInColumn,
        startX: clientX,
        startY: clientY,
        started: false,
      };
      setIsCursorGrabbing(true);

      const move = (e: MouseEvent) => {
        const pending = pendingDragRef.current;
        if (!pending) return;
        if (!pending.started) {
          const dist = Math.hypot(
            e.clientX - pending.startX,
            e.clientY - pending.startY,
          );
          if (dist < DRAG_THRESHOLD_PX) return;
          pending.started = true;
          ddRef.current.startDrag(pending.event, pending.mouseYInColumn);
        }
        ddRef.current.updateDrag(e.clientX, e.clientY, e.shiftKey);
      };

      const up = () => {
        try {
          if (pendingDragRef.current?.started) {
            ddRef.current.commitDrag();
            justDraggedRef.current = true;
            setTimeout(() => {
              justDraggedRef.current = false;
            }, 0);
          }
        } finally {
          pendingDragRef.current = null;
          setIsCursorGrabbing(false);
          removePointerListeners();
        }
      };

      moveListenerRef.current = move;
      upListenerRef.current = up;
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    },
    [removePointerListeners],
  );

  const handleResizeStart = useCallback(
    (event: PlanEvent, clientY: number, edge: "top" | "bottom") => {
      removePointerListeners();

      ddRef.current.startResize(event, edge);
      resizeStartYRef.current = clientY;
      setIsCursorGrabbing(true);

      const move = (e: MouseEvent) => {
        ddRef.current.updateResize(e.clientY - resizeStartYRef.current, e.shiftKey);
      };

      const up = () => {
        try {
          ddRef.current.commitResize();
          justDraggedRef.current = true;
          setTimeout(() => {
            justDraggedRef.current = false;
          }, 0);
        } finally {
          setIsCursorGrabbing(false);
          removePointerListeners();
        }
      };

      moveListenerRef.current = move;
      upListenerRef.current = up;
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    },
    [removePointerListeners],
  );

  // Cleanup des listeners si le composant se démonte pendant un drag
  useEffect(() => {
    return () => {
      removePointerListeners();
      setIsCursorGrabbing(false);
    };
  }, [removePointerListeners]);

  const slotColumnElRef = useRef<HTMLDivElement | null>(null);

  const handleSlotCreationMouseDown = useCallback(
    (date: string, mouseY: number, columnEl: HTMLDivElement) => {
      removePointerListeners();

      scRef.current.startSlotCreation(date, mouseY);
      slotColumnElRef.current = columnEl;

      const startY = mouseY;

      const move = (e: MouseEvent) => {
        const col = slotColumnElRef.current;
        if (!col) return;
        const rect = col.getBoundingClientRect();
        scRef.current.updateSlotCreation(e.clientY - rect.top, e.shiftKey);
      };

      const up = () => {
        try {
          const result = scRef.current.commitSlotCreation();
          if (result) {
            const col = slotColumnElRef.current;
            if (col) {
              const rect = col.getBoundingClientRect();
              onSlotCreatedRef.current(result.date, result.startTime, result.endTime, {
                colRight: rect.right,
                colLeft: rect.left,
                slotTop: rect.top + startY,
              });
            }
            justDraggedRef.current = true;
            setTimeout(() => {
              justDraggedRef.current = false;
            }, 0);
          }
        } finally {
          slotColumnElRef.current = null;
          removePointerListeners();
        }
      };

      moveListenerRef.current = move;
      upListenerRef.current = up;
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    },
    [removePointerListeners],
  );

  return {
    isCursorGrabbing,
    justDraggedRef,
    handleDragStart,
    handleResizeStart,
    handleSlotCreationMouseDown,
  };
}
